import { Controller } from '@hotwired/stimulus'

import { Chart } from 'chart.js'
import { CrosshairPlugin } from 'chartjs-plugin-crosshair'
import ChartjsPluginStacked100 from 'chartjs-plugin-stacked100'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { ChoroplethController, GeoFeature, ProjectionScale, ColorScale } from 'chartjs-chart-geo'
import { mergeDeepLeft } from 'ramda'

Chart.register(ChoroplethController, GeoFeature, ProjectionScale, ColorScale)

export default class extends Controller {
    static targets = ['canvas', 'options', 'chartContainer']

    optionsTargetConnected() {
        this.chart ? this.refreshChart() : this.initChart()
    }

    initChart() {
        this.optionsFromParam.plugins = []

        let opts = mergeDeepLeft(this.optionsFromParam, {
            options: this.defaultRetreaverChartOptions,
            plugins: [],
        })

        opts.data.labels = this.truncateLabels(this.optionsFromParam.data.labels)

        if (this.isEmpty) {
            opts.data = this.emptyData
        }

        if (this.enabledPlugins.includes('stacked100')) {
            opts.plugins.push(ChartjsPluginStacked100) // Adding js
        }

        if (this.enabledPlugins.includes('datalabels')) {
            opts.plugins.push(ChartDataLabels) // Adding js
        }

        if (opts.type === 'line') {
            opts.plugins.push(CrosshairPlugin)
        }

        this.chart = new Chart(this.canvasTarget, opts)
        this.#setChartContainerHeightByLabelHeight()
    }

    refreshChart() {
        this.chart.data = this.optionsFromParam.data
        this.chart.data.labels = this.truncateLabels(this.optionsFromParam.data.labels)

        if (this.isEmpty) {
            this.chart.data = this.emptyData
        }

        this.chart.update('none')
        this.#setChartContainerHeightByLabelHeight()
    }

    truncateLabels(labels) {
        this.originalLabelsByIndex = []
        let truncateLabels = []
        if (labels) {
            truncateLabels = [...labels]
            truncateLabels = truncateLabels.map((label, index) => {
                const truncateCount = 25
                let truncatedLabel = label
                if (label.length > truncateCount) {
                    truncatedLabel = `${label.substring(0, truncateCount)}...`
                }
                this.originalLabelsByIndex[index] = label
                return truncatedLabel
            })
        }

        return truncateLabels
    }

    // Vertical charts might have a ton of labels, so expand the height of the chart
    // to have 20px per label. Otherwise it will fill to the container height
    #setChartContainerHeightByLabelHeight() {
        if (this.chart.options.indexAxis === 'y') {
            const numberOfLabels = this.chart.data.labels.length
            const height = numberOfLabels * 25 // 25px is roughly the height of a chartjs label
            const minHeight = 387 // If label sizes fit within 24rem it will fill to the full container height
            if (height > minHeight) {
                this.chartContainerTarget.style.minHeight = `${height}px`
            }
        }
    }

    get isEmpty() {
        // if there is no dataset -> true
        // if there is no data in all datasets -> true
        // if there are only 0 in all data in all datasets -> true
        if (this.optionsFromParam.type != 'choropleth') {
            return (
                !this.optionsFromParam.data.datasets ||
                this.optionsFromParam.data.datasets.every(
                    dataset => !dataset.data || dataset.data.every(x => x == 0),
                )
            )
        }
        return false
    }

    get emptyData() {
        const emptyData = {
            labels: ['No Data'],
            datasets: [
                {
                    label: 'No Data',
                    // For pie and doughnut we want a round circle and that is why the data is [1]
                    data: ['pie', 'doughnut'].includes(this.optionsFromParam.type) ? [1] : [],
                    backgroundColor: ['#E0E0E0'], // Neutral color for empty state
                    borderColor: ['#B0B0B0'], // Border for empty state
                    borderWidth: 1,
                },
            ],
        }

        return emptyData
    }

    get optionsFromParam() {
        return JSON.parse(this.optionsTarget.textContent)
    }
    get enabledPlugins() {
        return JSON.parse(this.optionsTarget.textContent).plugins || []
    }
    get shouldSortData() {
        return this.optionsFromParam.shouldSortData || false
    }

    get defaultRetreaverChartOptions() {
        const options = {
            maintainAspectRatio: false,
            responsive: true,
            animation: {
                onComplete: (chart) => {
                    if (!this.chartRendered) {
                        // Broadcast rendered event, useful for cross-controller coordination
                        this.dispatch('rendered', { detail: { chart } })
                        this.chartRendered = true
                    }
                },
            },
            plugins: {
                legend: {
                    display: true,
                    position: 'top',
                    onClick: (e, legendItem, legend) => {
                        // keep default toggle
                        Chart.defaults.plugins.legend.onClick.call(this, e, legendItem, legend)

                        // We need this for two things.
                        // 1 - We need to set it up on the next frame, because hiding is async, we could use 'requestAnimationFrame'
                        // 2 - 550 is a stylistic choice where you see the hiding and see them re-arranging.
                        setTimeout(() => {
                            this.#sortAndReorderData()
                            this.chart.update()
                        }, 550)
                    },
                },
                tooltip: {
                    callbacks: {
                        beforeLabel: (tooltipItem) => {
                            // data can come in three different ways raw.value, raw.x, raw
                            // The way what we need to provide this in order to work is:
                            // datasets: [ { label: "...", data: ...., formatter: 'dollar'} ]

                            const formatter = tooltipItem.dataset.formatter
                            const raw = tooltipItem.raw
                            const selectedValue = (raw && typeof raw === 'object')
                                ? (raw.value ?? raw.x ?? raw.y ?? raw)
                                : raw
                            const numericValue = typeof selectedValue === 'number' ? selectedValue : Number(selectedValue)

                            tooltipItem.formattedValue = this.#formatNumber(numericValue, formatter)
                        },
                        title: (context) => {
                            const index = context[0]?.dataIndex
                            return this.originalLabelsByIndex[index] || context[0].label
                        },
                    },
                },
            },
            pointStyle: false,
            cubicInterpolationMode: 'monotone',
            tension: 0.4,
        }

        if (this.optionsFromParam.type == 'line') {
            // This is for the plugins crosshair
            options.plugins.crosshair = {
                line: {
                    color: '#F66',
                    width: 1,
                },
                sync: {
                    enabled: false,
                },
                zoom: {
                    enabled: false,
                },
            }
        }

        if (['choropleth'].includes(this.optionsFromParam.type)) {
            options.plugins.legend.display = false
            options.plugins.tooltip.callbacks.title = (context) => {
                    // NOTE: The tooltip title should be the state name -> Texas.
                    return context[0]?.raw?.feature?.properties?.name || ''
                }

            options.scales = {
                projection: {
                    axis: 'x',
                    projection: 'albersUsa',
                },
                color: {
                    axis: 'x',
                    missing: 'white',
                    legend: {
                        position: 'bottom-right',
                        align: 'right',
                    },
                },
            }
        }

        if (this.enabledPlugins.includes('datalabels')) {
            options.layout = { padding: { right: 40 } }

            options.plugins.datalabels = {
                padding: 0,
                font: { weight: 600, size: 11 },

                // put it at the very end of the stacked row
                anchor: 'end',
                align: 'end',
                offset: 4,
                clamp: true,
                clip: false,
                display: (ctx) => {
                    // only on the last *visible* dataset of this stack
                    const { chart, dataset } = ctx
                    let lastVisible = 1
                    chart.data.datasets.forEach((ds, i) => {
                        if (chart.isDatasetVisible(i) && ds.stack === dataset.stack) lastVisible = i
                    })
                    return ctx.datasetIndex === lastVisible
                },
                formatter: (value, ctx) => {
                    // sum only visible datasets in this stack for this row
                    const { chart, dataset, dataIndex } = ctx

                    let sum = 0
                    chart.data.datasets.forEach((ds, i) => {
                        if (chart.isDatasetVisible(i) && ds.stack === dataset.stack) {
                            const v = ds.data[dataIndex] ?? 0
                            sum += typeof v === 'object' ? v.x ?? v.y ?? 0 : v
                        }
                    })

                    // We need the same formatter for all datasets in order to format the summed datalabel
                    let formatter;
                    const formatters = [...new Set(chart.data.datasets.map(ds => ds.formatter))]
                    if (formatters.length > 1) {
                        console.error("There are more than one formatter and cannot apply format: ", formatters)
                    } else {
                        formatter = formatters[0];
                    }

                    return this.#formatNumber(sum, formatter)
                },
            }

            if (['pie', 'doughnut'].includes(this.optionsFromParam.type)) {
                // # Easier to override the above config
                options.plugins.datalabels.anchor = null
                options.plugins.datalabels.color = 'white'
                options.plugins.datalabels.display = 'auto'
                options.plugins.datalabels.backgroundColor = context =>
                    context.dataset.backgroundColor
            }
        }

        if (this.enabledPlugins.includes('stacked100')) {
            options.plugins.stacked100 = {
                enable: true,
            }
            options.indexAxis = 'y'
            options.scales = {
                x: {
                    type: 'logarithmic',
                },
            }
        }

        if (['pie', 'doughnut'].includes(this.optionsFromParam.type)) {
            options.plugins.tooltip = {
                // This is for showing how many % is the piece of the pie.
                callbacks: {
                    label(context) {
                        // Some of the data points might be turned off. We need to calculate % base on only the turned on
                        const { legendItems } = context.chart.legend

                        let total = 0
                        if (legendItems.length != 0) {
                            const enabledIndexes = Object.keys(legendItems).filter(
                                index => !legendItems[index].hidden,
                            )
                            enabledIndexes.forEach(index => (total += context.dataset.data[index]))
                        } else {
                            // There could be 0 legend items so we need to fallback to everything is enabled
                            total = context.dataset.data.reduce((sum, value) => sum + value, 0)
                        }

                        const value = context.raw
                        const percentage = ((value / total) * 100).toFixed(2)
                        const { tooltipValues } = context.chart.config.data
                        const tooltipValue =
                            (tooltipValues && tooltipValues[context.dataIndex]) ?? value
                        return `${tooltipValue} (${percentage}%)`
                    },
                },
            }
        }

        return options
    }

    #sortAndReorderData() {
        if (!this.shouldSortData) {
            return
        }

        // Computing the order of the indexes of all visible data
        const labelsLength = this.chart.data.labels.length
        const keys = new Array(labelsLength).fill(0)
        this.chart.data.datasets.forEach((_, idx) => {
            if (!this.chart.isDatasetVisible(idx)) {
                return
            }
            for (let i = 0; i < labelsLength; i++) {
                keys[i] += +this.optionsFromParam.data.datasets[idx].data[i] || 0
            }
        })
        const order = [...keys.keys()].sort((a, b) => keys[b] - keys[a])

        // Reordering Labels while keeping the full label reference list in sync
        const reorderedOriginalLabels = order.map(i => this.optionsFromParam.data.labels[i])
        this.originalLabelsByIndex = reorderedOriginalLabels
        this.chart.data.labels = reorderedOriginalLabels.map(label => this.#truncateLabel(label))

        // Reordering data inside each dataset
        this.chart.data.datasets.forEach((ds, idx) => {
            ds.data = order.map(i => this.optionsFromParam.data.datasets[idx].data[i])
        })
    }

    #truncateLabel(label) {
        const truncateCount = 25
        if (typeof label !== 'string') {
            return label
        }
        return label.length > truncateCount ? `${label.substring(0, truncateCount)}...` : label
    }

    #formatNumber(value, formatter) {
        if (formatter == "dollar"){
            return this.#formatDollarNumber(value)
        } else {
            return value.toLocaleString()
        }
    }

    /**
     * Format a number as USD using truncation (not rounding) to two decimals.
     * Mirrors the ClickHouse logic: sign + '$' + grouped dollars + '.' + two-digit cents.
     *
     * @param {number} value
     * @returns {string}
     */
    #formatDollarNumber(value) {
        if (!Number.isFinite(value)) return String(value); // NaN/Infinity pass-through

        // Truncate to cents (toward zero), like intDiv/toUInt64 in the original
        const absCents = Math.trunc(Math.abs(value) * 100);
        const dollars  = Math.trunc(absCents / 100);
        const cents    = absCents % 100;

        // Add thousands separators
        const dollarsStr = String(dollars).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

        // Prefix sign exactly like the SQL: '-$' only when value < 0 (not for -0)
        const sign = value < 0 ? '-$' : '$';

        return `${sign}${dollarsStr}.${String(cents).padStart(2, '0')}`;
    }
}
